import { ref, computed, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import apiClient, { getErrorMessage } from '@/api/apiClient';
import { useLoading } from '@/composables/useLoading';
import { useOnLocaleChange } from '@/composables/useOnLocaleChange';
import { t, entityParams } from '@/i18n';

// A translation key (not the text) so the message follows the language when it changes
const NO_DATA_MESSAGE = 'expenses.noData';

// Local calendar date as YYYY-MM-DD (toISOString would convert to UTC and can shift the day)
const toIsoDate = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// The date range each preset stands for, computed fresh every time it's picked (so "This
// month"/"This year" always mean the current one, not whatever it was when the page loaded).
// 'custom' has no range of its own: it keeps whatever dates the user last typed.
export const PRESETS = {
    thisMonth: () => {
        const now = new Date();
        return { startDate: toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: toIsoDate(now) };
    },
    last3Months: () => {
        const now = new Date();
        return { startDate: toIsoDate(new Date(now.getFullYear(), now.getMonth() - 2, 1)), endDate: toIsoDate(now) };
    },
    thisYear: () => {
        const now = new Date();
        return { startDate: toIsoDate(new Date(now.getFullYear(), 0, 1)), endDate: toIsoDate(now) };
    },
    all: () => ({ startDate: null, endDate: null }),
};

// Reads a boolean saved in localStorage ('true' / 'false'), falling back to a default
const readStoredBoolean = (key, fallback) => {
    const stored = localStorage.getItem(key);
    return stored === null ? fallback : stored === 'true';
};

// Everything the Expenses page needs: filters, sorting, pagination, data fetching,
// and the edit/delete flows. The view only wires this to the template.
export function useExpensesList() {
    const toast = useToast();
    const { loading, track } = useLoading();

    // Data
    const expensesData = ref([]);
    const totalExpense = ref(0);
    const subcategories = ref([]);
    const noDataMessage = ref('');

    // Filters (some are remembered between visits)
    const thisMonthRange = PRESETS.thisMonth();
    const startDate = ref(thisMonthRange.startDate);
    const endDate = ref(thisMonthRange.endDate);
    const selectedSubcategory = ref(null);
    const recurringFilter = ref(localStorage.getItem('recurringFilter') || 'all');
    const searchTerm = ref('');
    // showNoteColumn is the draft (bound to the checkbox); appliedShowNoteColumn is what the
    // table actually reads, kept in sync with the other filters (Apply on mobile, live on
    // desktop) even though it never touches the network — see applyFilters()
    const showNoteColumn = ref(readStoredBoolean('showNoteColumn', true));
    const appliedShowNoteColumn = ref(showNoteColumn.value);
    const showFilters = ref(false);

    // Sorting and pagination
    const selectedSortOption = ref('id');
    const selectedSortDirection = ref('DESC');
    const currentPage = ref(1);
    const totalPages = ref(0);
    const itemsPerPage = ref(20);

    // Modals
    const isAddModalOpen = ref(false);
    const isEditModalOpen = ref(false);
    const selectedExpense = ref(null);

    // Snapshot of the filters actually behind what's on screen (and what a fetch just used):
    // date range/category/type/search only change this on Apply, so anything reading it (export)
    // always matches the table, never a filter the user picked but hasn't applied yet.
    const appliedFilters = ref({
        startDate: startDate.value,
        endDate: endDate.value,
        selectedSubcategory: selectedSubcategory.value,
        recurringFilter: recurringFilter.value,
        searchTerm: searchTerm.value,
    });

    const syncAppliedFilters = () => {
        appliedFilters.value = {
            startDate: startDate.value,
            endDate: endDate.value,
            selectedSubcategory: selectedSubcategory.value,
            recurringFilter: recurringFilter.value,
            searchTerm: searchTerm.value,
        };
    };

    // Computed -----------------------------------------------------------
    const groupedSubcategories = computed(() => {
        const grouped = {};
        subcategories.value.forEach((subcategory) => {
            if (!grouped[subcategory.cat]) {
                grouped[subcategory.cat] = [];
            }
            grouped[subcategory.cat].push(subcategory);
        });
        return grouped;
    });

    // Uses the applied search term, not the draft: on mobile, typing must not change what's on
    // screen until Apply is clicked, same as every other filter (the server itself already
    // filters by search term on Apply; this is just a client-side mirror of that same term).
    const filteredExpenses = computed(() => {
        const appliedSearchTerm = appliedFilters.value.searchTerm;
        if (!appliedSearchTerm) {
            return expensesData.value;
        }
        const term = appliedSearchTerm.toLowerCase();
        return expensesData.value.filter((expense) => (
            (expense.note && expense.note.toLowerCase().includes(term)) ||
            (expense.catname && expense.catname.toLowerCase().includes(term)) ||
            (expense.subname && expense.subname.toLowerCase().includes(term))
        ));
    });

    const expenseColumns = computed(() => {
        const columns = [
            { key: "id", label: t('fields.id'), sort: true },
            { key: "subname", label: t('fields.subcategory'), sort: false },
            { key: "date", label: t('fields.date'), sort: true },
            { key: "value", label: t('fields.value'), sort: true }
        ];

        if (appliedShowNoteColumn.value) {
            // Note goes right after the date
            columns.splice(2, 0, { key: "note", label: t('fields.note'), sort: false });
        }

        return columns;
    });

    // Data fetching --------------------------------------------------------
    const fetchSubcategories = () => {
        apiClient.get('/get-subcategories.php')
            .then((response) => {
                subcategories.value = response.data;
            })
            .catch((error) => {
                console.error('Error fetching subcategories:', error);
            });
    };

    const fetchExpensesData = () => {
        // Always the *applied* filters, never the draft: handleSortChange/handlePageChange (and
        // the post-edit/delete refreshes below) call this directly, without going through
        // applyFilters(). If they read the draft refs instead, an unapplied filter edit sitting
        // in the panel on mobile would silently leak into the request just by sorting a column
        // or turning the page — before the user ever clicked "Apply".
        const requestData = {
            sortOption: selectedSortOption.value,
            sortDirection: selectedSortDirection.value,
            startDate: appliedFilters.value.startDate,
            endDate: appliedFilters.value.endDate,
            selectedSubcategory: appliedFilters.value.selectedSubcategory,
            currentPage: currentPage.value,
            itemsPerPage: itemsPerPage.value,
            recurringFilter: appliedFilters.value.recurringFilter,
            searchTerm: appliedFilters.value.searchTerm
        };

        return track(
            apiClient.post('/get-expenses.php', requestData)
                .then((response) => {
                    expensesData.value = response.data.expensesData;
                    totalExpense.value = response.data.totalExpense;
                    totalPages.value = response.data.totalPages;

                    if (response.data.expensesData.length <= 0) {
                        noDataMessage.value = NO_DATA_MESSAGE;
                    }

                    // The current page may no longer exist (e.g. the last expense of the last page was
                    // deleted): go to the new last page instead of showing an empty table
                    if (totalPages.value >= 1 && currentPage.value > totalPages.value) {
                        currentPage.value = totalPages.value;
                        return fetchExpensesData();
                    }
                })
                .catch((error) => {
                    console.error('Error fetching expenses:', error);
                })
        );
    };

    // Filter and sort handlers ---------------------------------------------
    // Changing a filter only updates the draft value: nothing is fetched until the user
    // confirms with "Apply" (a live fetch per keystroke/selection is bad on mobile, where the
    // filter panel takes over the screen). Applying always goes back to the first page and
    // collapses the filter panel, handing the screen back to the (now updated) table.
    const applyFilters = () => {
        currentPage.value = 1;
        syncAppliedFilters();
        appliedShowNoteColumn.value = showNoteColumn.value;
        fetchExpensesData();
        showFilters.value = false;
    };

    // The datepicker emits [start, end] as YYYY-MM-DD strings, or null when cleared ("all time")
    const updateDateRange = (range) => {
        startDate.value = range ? range[0] : null;
        endDate.value = range ? range[1] : null;
    };

    const updateSubcategory = (subcategoryId) => {
        selectedSubcategory.value = subcategoryId;
    };

    const updateRecurringFilter = (value) => {
        recurringFilter.value = value;
        localStorage.setItem('recurringFilter', value);
    };

    const updateShowNoteColumn = (value) => {
        showNoteColumn.value = value;
        localStorage.setItem('showNoteColumn', value);
    };

    const updateSearchTerm = (value) => {
        searchTerm.value = value;
    };

    // Clearing the search is a decisive action on its own (unlike typing), so it takes effect
    // right away instead of waiting for "Apply"
    const clearSearchTerm = () => {
        searchTerm.value = '';
        applyFilters();
    };

    const handleSortChange = (sortOption, sortDirection) => {
        selectedSortOption.value = sortOption;
        selectedSortDirection.value = sortDirection;
        fetchExpensesData();
    };

    const handlePageChange = (page) => {
        currentPage.value = page;
        fetchExpensesData();
    };

    const toggleFilters = () => {
        showFilters.value = !showFilters.value;
    };

    // Add / edit / delete -----------------------------------------------------
    const openAddModal = () => {
        fetchSubcategories();
        isAddModalOpen.value = true;
    };

    const editExpense = (expense) => {
        selectedExpense.value = expense;
        isEditModalOpen.value = true;
    };

    const deleteExpense = (expense) => {
        if (!window.confirm(t('crud.confirmDelete', entityParams('expense')))) {
            return;
        }

        apiClient.post('/delete-expense.php', { expense })
            .then((response) => {
                if (response.data.success === true) {
                    fetchExpensesData();
                    toast.success(t('crud.deleted', entityParams('expense')));
                } else {
                    console.error('Failed to delete expense.');
                    toast.error(response.data.message || t('crud.failedDelete', entityParams('expense')));
                }
            })
            .catch((error) => {
                console.error('Error deleting expense:', error);
                toast.error(t('crud.errorDeleting', { ...entityParams('expense'), detail: getErrorMessage(error) }));
            });
    };

    onMounted(() => {
        fetchSubcategories();
        fetchExpensesData();
    });

    // category and subcategory names come from the API in the current language
    useOnLocaleChange(() => {
        fetchSubcategories();
        fetchExpensesData();
    });

    return {
        loading,
        // data
        totalExpense,
        groupedSubcategories,
        filteredExpenses,
        expenseColumns,
        noDataMessage,
        // filters
        startDate,
        endDate,
        selectedSubcategory,
        recurringFilter,
        searchTerm,
        showNoteColumn,
        showFilters,
        appliedFilters,
        // sorting and pagination
        selectedSortOption,
        selectedSortDirection,
        currentPage,
        totalPages,
        // modals
        isAddModalOpen,
        isEditModalOpen,
        selectedExpense,
        // handlers
        fetchExpensesData,
        updateDateRange,
        updateSubcategory,
        updateRecurringFilter,
        updateShowNoteColumn,
        updateSearchTerm,
        clearSearchTerm,
        applyFilters,
        handleSortChange,
        handlePageChange,
        toggleFilters,
        openAddModal,
        editExpense,
        deleteExpense
    };
}
